/**
 * DuplicateMergeModal — Side-by-Side Merge-Preview für N Kontakte (N ≥ 2).
 *
 * UX-Best-Practice (HubSpot Merge / Salesforce Merge Contacts):
 *   - Alle beteiligten Kontakte nebeneinander mit den identity-relevanten
 *     Feldern.
 *   - User wählt per Radio den Winner ("Diesen Kontakt behalten"). Alle
 *     anderen werden nacheinander in den Winner gemergt.
 *   - Klare Vorschau: was passiert nach dem Merge (E-Mails union, Loser
 *     verschwinden aus Kontaktliste, historische Sends + Reminders +
 *     Touchpoints wandern mit, Notizen und Custom Fields werden union'd).
 *   - Sequentielle Merge-Calls mit Progress-Indikator ("2/3 zusammengeführt"),
 *     damit ein einzelner Fehler nicht die ganze Batch abbricht.
 *
 * V2 (Phase E-Fix): Multi-Way-Support statt nur 2-Way. Der `contactIds`-Prop
 * ist eine Liste. Nach Auswahl des Winners startet ein sequentieller Loop,
 * der pro Loser einen `mergeContactPair`-Call macht. Bei Fehlern wird der
 * fehlgeschlagene Kontakt markiert, der Rest läuft trotzdem durch.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import './DuplicateMergeModal.css';

// Nur befüllte Felder werden gerendert — leere Platzhalter blähen die Card
// künstlich auf und lenken vom Wesentlichen ab (Status + Kollisions-Email).
const DETAIL_FIELDS = [
  { key: 'lead_website',    label: 'Website' },
  { key: 'lead_telephone',  label: 'Phone' },
  { key: 'lead_industry',   label: 'Industry' },
  { key: 'lead_country',    label: 'Country' },
  { key: 'lead_city',       label: 'City' },
];

// Status-Farb/Icon-Palette (gespiegelt aus NewWorldDbStatusFilter).
const STATUS_STYLES = {
  'Not Contacted':     { color: '#94a3b8', icon: 'fa-circle',            label: 'Not Contacted',    sent: false },
  'Lead':              { color: '#3b82f6', icon: 'fa-bullseye',          label: 'Lead',             sent: false },
  'In Outreach':       { color: '#6366f1', icon: 'fa-satellite-dish',    label: 'In Outreach',      sent: false },
  'Introduced':        { color: '#8b5cf6', icon: 'fa-paper-plane',       label: 'Introduced',       sent: true  },
  'First Follow-up':   { color: '#a78bfa', icon: 'fa-reply',             label: '1st Follow-Up',    sent: true  },
  'Second Follow-up':  { color: '#c4b5fd', icon: 'fa-reply-all',         label: '2nd Follow-Up',    sent: true  },
  'Third Follow-up':   { color: '#ddd6fe', icon: 'fa-forward',           label: '3rd Follow-Up',    sent: true  },
  'replied':           { color: '#10b981', icon: 'fa-comment-dots',      label: 'Replied',          sent: true  },
  'Interested':        { color: '#22c55e', icon: 'fa-circle-check',      label: 'Interested',       sent: true  },
  'Not Interested':    { color: '#ef4444', icon: 'fa-circle-xmark',      label: 'Not Interested',   sent: true },
  'Outreach: Removed': { color: '#f59e0b', icon: 'fa-user-minus',        label: 'Outreach Removed', sent: false },
  'Outreach: Failed':  { color: '#ef4444', icon: 'fa-exclamation-triangle', label: 'Outreach Failed', sent: false },
};
function getStatusStyle(raw) {
  if (!raw) return STATUS_STYLES['Not Contacted'];
  return STATUS_STYLES[raw] || { color: '#64748b', icon: 'fa-tag', label: String(raw), sent: false };
}
function hexToRgba(hex, alpha) {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return `rgba(148,163,184,${alpha})`;
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const num = parseInt(full, 16);
  const r = (num >> 16) & 255, g = (num >> 8) & 255, b = num & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}
function formatRelative(msOrIso) {
  if (!msOrIso) return '';
  const t = typeof msOrIso === 'string' ? Date.parse(msOrIso) : Number(msOrIso);
  if (!t || Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  const day = 86400000;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.round(diff / 60000)} min ago`;
  if (diff < day) return `${Math.round(diff / 3600000)} h ago`;
  if (diff < 7 * day) return `${Math.round(diff / day)} days ago`;
  return new Date(t).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
}

function seedContactMap(src, ids) {
  const map = {};
  if (!src) return map;
  ids.forEach((id) => { if (src[id]) map[id] = src[id]; });
  return map;
}
function hasAllContacts(map, ids) {
  return ids.length >= 2 && ids.every((id) => map && map[id]);
}
function pickDefaultWinner(map, ids) {
  const SCORE_FIELDS = ['lead_email', 'lead_website', 'lead_telephone',
    'lead_industry', 'lead_country', 'lead_city', 'lead_decision_makers',
    'lead_socials'];
  const scoreOf = (d) => {
    const filled = SCORE_FIELDS.filter((k) => hasValue(d && d[k])).length;
    const contactedBonus = (d && d.lead_contacted_timestamp) ? 2 : 0;
    return filled + contactedBonus;
  };
  const scored = ids
    .map((id) => ({ id, score: scoreOf(map[id]) }))
    .sort((a, b) => b.score - a.score);
  return (scored[0] && scored[0].id) || ids[0] || null;
}

export default function DuplicateMergeModal({
  userId, alertId, contactIds, initialContacts,
  onClose, onBack, onMerged, onBusy, embedded = false,
}) {
  // Backward-compat: falls jemand noch die alte 2-Arg-API verwendet.
  const ids = useMemo(
    () => (Array.isArray(contactIds) ? contactIds : []),
    [contactIds],
  );

  const [contacts, setContacts] = useState(() => seedContactMap(initialContacts, ids));
  const [winnerId, setWinnerId] = useState(() => {
    const seeded = seedContactMap(initialContacts, ids);
    return hasAllContacts(seeded, ids) ? pickDefaultWinner(seeded, ids) : (ids[0] || null);
  });
  const [loading, setLoading] = useState(() => !hasAllContacts(seedContactMap(initialContacts, ids), ids));
  const [error, setError] = useState(null);
  const [merging, setMerging] = useState(false);
  const [progress, setProgress] = useState(null);  // { done, total }
  const [failures, setFailures] = useState([]);
  const [done, setDone] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    onBusy?.(merging);
    return () => onBusy?.(false);
  }, [merging, onBusy]);

  useEffect(() => {
    if (ids.length < 2) {
      setError('At least two contacts must be provided.');
      setLoading(false);
      return undefined;
    }
    if (hasAllContacts(contacts, ids)) {
      setLoading(false);
      return undefined;
    }
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const db = getFirestore();
        const snaps = await Promise.all(
          ids.map((id) => getDoc(doc(db, `World/${userId}/db/${id}`))),
        );
        if (cancel) return;
        const map = {};
        snaps.forEach((s, i) => {
          if (s.exists()) map[ids[i]] = { id: s.id, ...s.data() };
        });
        const missing = ids.filter((id) => !map[id]);
        if (missing.length > 0) {
          throw new Error(`Contacts not found: ${missing.join(', ')}`);
        }
        setContacts(map);
        setWinnerId(pickDefaultWinner(map, ids));
      } catch (err) {
        if (!cancel) setError(err && err.message ? err.message : String(err));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, ids.join('|')]);

  const winner = contacts[winnerId];
  const loserIds = useMemo(() => ids.filter((id) => id !== winnerId), [ids, winnerId]);

  const mergedEmails = useMemo(() => {
    if (!winner || loserIds.length === 0) return '';
    let acc = winner.lead_email || '';
    for (const lid of loserIds) {
      const l = contacts[lid];
      if (!l) continue;
      acc = unionEmailList(acc, l.lead_email);
    }
    return acc;
  }, [winner, loserIds, contacts]);

  // Kollidierende E-Mail(s): E-Mails, die auf mindestens zwei der Kontakte
  // vorkommen. Der DuplicateMergeModal kennt den Original-Alert nicht immer
  // (kann von DuplicateActionModal, ActionCenter oder direkt geöffnet
  // werden), also leiten wir die Kollision aus den Contact-Daten selbst ab.
  const collisionEmails = useMemo(() => {
    const emailToOwners = new Map();
    ids.forEach((cid) => {
      const c = contacts[cid];
      if (!c || typeof c.lead_email !== 'string') return;
      c.lead_email.split(/[,;]/).forEach((raw) => {
        const key = String(raw || '').trim().toLowerCase();
        if (!key) return;
        if (!emailToOwners.has(key)) emailToOwners.set(key, new Set());
        emailToOwners.get(key).add(cid);
      });
    });
    return new Set(
      [...emailToOwners.entries()]
        .filter(([, owners]) => owners.size > 1)
        .map(([email]) => email),
    );
  }, [ids, contacts]);

  const handleMerge = async () => {
    setMerging(true);
    setError(null);
    setFailures([]);
    const call = httpsCallable(functions, 'mergeContactPair');
    const local = [];
    let successCount = 0;
    for (let i = 0; i < loserIds.length; i++) {
      const loserId = loserIds[i];
      setProgress({ done: i, total: loserIds.length, currentLoserId: loserId });
      try {
        const res = await call({
          userId,
          winnerId,
          loserId,
          // Der Alert wird beim ERSTEN Merge auf 'merged' gesetzt. Bei
          // weiteren Merges lassen wir das Alert-Update weg — der Alert
          // ist bereits geschlossen und ein zweiter Update-Versuch wäre
          // redundant (aber idempotent, würde also nicht schaden).
          alertId: i === 0 ? alertId : null,
        });
        if (!res.data || !res.data.ok) throw new Error('Merge-Antwort nicht ok');
        successCount += 1;
      } catch (err) {
        console.error(`[DuplicateMergeModal] merge ${winnerId}<-${loserId} failed:`, err);
        local.push({ loserId, error: err && err.message ? err.message : 'Error' });
        setFailures((prev) => [...prev, { loserId, error: err && err.message ? err.message : 'Error' }]);
      }
    }
    setProgress({ done: loserIds.length, total: loserIds.length });
    setMerging(false);
    setDone(true);
    // Bei mindestens einem erfolgreichen Merge: parent informieren.
    if (successCount > 0) {
      setTimeout(() => {
        onMerged && onMerged({ successCount, failures: local });
        if (local.length === 0) onClose && onClose();
      }, 1500);
    }
  };

  const sheet = (
      <div className="dup-merge dup-merge-sheet" onClick={embedded ? undefined : (e) => e.stopPropagation()}>
        <div className="dup-merge-header">
          <div className="dup-merge-headerCopy">
            <h2 className="dup-merge-title">
              {ids.length === 2 ? 'Merge Contacts' : `Merge ${ids.length} Contacts`}
            </h2>
            <p className="dup-merge-subtitle">
              Choose which contact stays. History, notes and activities move with the merge.
            </p>
          </div>
          {!merging && !done && (
            <button type="button" onClick={onClose} className="dup-merge-close" aria-label="Close">×</button>
          )}
        </div>

        <div className="dup-merge-body">
          {loading && (
            <div className="dup-merge-contacts" aria-busy="true">
              {Array.from({ length: Math.max(ids.length, 2) }).map((_, i) => (
                <MergeCardSkeleton key={`skel-${i}`} delay={i * 90} />
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="dup-merge-center dup-merge-center--error">{error}</div>
          )}

          {!loading && !error && Object.keys(contacts).length === ids.length && (
            <>
              <div className="dup-merge-contacts">
                {ids.map((id) => (
                  <ContactColumn
                    key={id}
                    contact={contacts[id]}
                    selected={winnerId === id}
                    onSelect={() => !merging && setWinnerId(id)}
                    disabled={merging || done}
                    failed={failures.some((f) => f.loserId === id)}
                    collisionEmails={collisionEmails}
                  />
                ))}
              </div>

              <div className="dup-merge-preview">
                <button
                  type="button"
                  onClick={() => setPreviewOpen((v) => !v)}
                  className="dup-merge-previewHead"
                  aria-expanded={previewOpen}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span className="dup-merge-previewKicker">After the merge</span>
                    <span className="dup-merge-previewText">
                      <strong>{winner?.business_name || '(no name)'}</strong>{' '}
                      will remain — the other{' '}
                      {loserIds.length === 1
                        ? '1 contact will be'
                        : `${loserIds.length} contacts will be`}{' '}
                      merged into it.
                    </span>
                  </span>
                  <span className={`dup-merge-chevron${previewOpen ? ' dup-merge-chevron--open' : ''}`}>
                    <i className="fa fa-chevron-down" aria-hidden />
                  </span>
                </button>

                {previewOpen && (
                  <div className="dup-merge-previewBody">
                    <div className="dup-merge-rules">
                      <PreviewRule icon="fa-plus" title="Added (union)">
                        Emails, phone numbers, social links, decision makers —
                        every new address/number/platform from the other contacts is
                        added to the main contact's list.
                      </PreviewRule>
                      <PreviewRule icon="fa-fill" title="Filled in only if empty">
                        Website, description, industry, address, custom fields — anything
                        already set on the main contact stays untouched; only empty fields
                        are filled from the other contact.
                      </PreviewRule>
                      <PreviewRule icon="fa-clock-rotate-left" title="Transferred fully">
                        Notes, sent emails, touchpoints, reminders and
                        flow history are re-linked to the main contact.
                      </PreviewRule>
                      <PreviewRule icon="fa-eye-slash" title="Disappears from the list">
                        The other contacts are marked as "Merged" and hidden from the
                        table — the data itself is not lost.
                      </PreviewRule>
                    </div>

                    <div className="dup-merge-emailStrip">
                      <span className="dup-merge-emailLabel">New email list on main contact</span>
                      <code className="dup-merge-code">{mergedEmails || '(none)'}</code>
                    </div>
                  </div>
                )}
              </div>

              {progress && (
                <div className="dup-merge-progress">
                  {progress.done < progress.total
                    ? `Merging… ${progress.done + 1}/${progress.total}`
                    : `Done: ${progress.done}/${progress.total} processed`}
                  <div className="dup-merge-progressBar">
                    <div
                      className="dup-merge-progressFill"
                      style={{ width: `${Math.round((progress.done / Math.max(progress.total, 1)) * 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {failures.length > 0 && (
                <div className="dup-merge-failures">
                  <strong>{failures.length} contact{failures.length === 1 ? '' : 's'} not merged:</strong>
                  <ul>
                    {failures.map((f) => (
                      <li key={f.loserId}>{String(f.loserId).slice(0, 10)}…: {f.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <div className="dup-merge-footer">
          {!done && (
            <button
              type="button"
              onClick={() => (onBack || onClose)?.()}
              disabled={merging}
              className="dup-merge-btn dup-merge-btn--ghost"
            >
              <i className="fa fa-arrow-left" aria-hidden />
              Go Back
            </button>
          )}
          <button
            type="button"
            onClick={handleMerge}
            disabled={loading || merging || done || !!error || ids.length < 2}
            className="dup-merge-btn dup-merge-btn--primary"
          >
            {merging
              ? 'Merging…'
              : done
                ? `Done${failures.length > 0 ? ` (with ${failures.length} errors)` : ''}`
                : ids.length === 2 ? 'Merge' : `Merge all ${loserIds.length} into main`}
          </button>
        </div>
      </div>
  );

  if (embedded) return sheet;

  const node = (
    <div className="dup-merge-overlay" onClick={merging ? undefined : onClose}>
      <div className="dup-overlay-slot">{sheet}</div>
    </div>
  );

  if (typeof document === 'undefined') return node;
  return createPortal(node, document.body);
}

function PreviewRule({ icon, title, children }) {
  return (
    <div className="dup-merge-rule">
      <div className="dup-merge-ruleIcon"><i className={`fa ${icon}`} aria-hidden /></div>
      <div>
        <div className="dup-merge-ruleTitle">{title}</div>
        <div className="dup-merge-ruleDesc">{children}</div>
      </div>
    </div>
  );
}

function MergeCardSkeleton({ delay = 0 }) {
  return (
    <div className="dup-merge-card dup-merge-card--skel" style={{ animationDelay: `${delay}ms` }}>
      <div className="dup-skel dup-skel--pill" />
      <div className="dup-skel dup-skel--title" />
      <div className="dup-skel dup-skel--pill" />
      <div className="dup-skel dup-skel--email" />
      <div className="dup-merge-skelDetails">
        <div className="dup-skel dup-skel--line" />
        <div className="dup-skel dup-skel--line dup-skel--lineShort" />
      </div>
    </div>
  );
}

// Kleine Helper zum sicheren Parsen der JSON-Stringfelder auf Kontakten.
function parseJsonSafe(v, fallback) {
  if (v == null || v === '') return fallback;
  if (typeof v !== 'string') return v;
  try { return JSON.parse(v); } catch (_e) { return fallback; }
}
const SOCIAL_ICON = {
  linkedin: 'fa-linkedin', instagram: 'fa-instagram', facebook: 'fa-facebook',
  twitter: 'fa-twitter', x: 'fa-x-twitter', tiktok: 'fa-tiktok',
  youtube: 'fa-youtube', pinterest: 'fa-pinterest', xing: 'fa-xing',
};
function toEpochMs(v) {
  if (!v) return 0;
  if (typeof v === 'number') return v > 1e12 ? v : v * 1000;
  if (typeof v === 'string') { const t = Date.parse(v); return Number.isFinite(t) ? t : 0; }
  if (typeof v === 'object' && v.seconds) return v.seconds * 1000;
  return 0;
}

function ContactColumn({ contact, selected, onSelect, disabled, failed, collisionEmails }) {
  const emails = String(contact.lead_email || '')
    .split(/[,;]/).map((s) => s.trim()).filter(Boolean);
  const collisionSet = collisionEmails || new Set();
  const primaryEmail = emails.find((e) => collisionSet.has(e.toLowerCase())) || emails[0] || '';
  const extraEmailCount = Math.max(emails.length - 1, 0);

  const detailRows = DETAIL_FIELDS
    .map((f) => ({ ...f, value: contact[f.key] }))
    .filter((f) => hasValue(f.value));

  const statusStyle = getStatusStyle(contact.lead_contacted);
  const wasContacted = statusStyle.sent || !!contact.lead_contacted_timestamp;
  const contactedRel = formatRelative(contact.lead_contacted_timestamp);

  const notesCount = Array.isArray(contact.lead_notes) ? contact.lead_notes.length : 0;
  const dms = parseJsonSafe(contact.lead_decision_makers, []) || [];
  const dmCount = Array.isArray(dms) ? dms.length : 0;
  const flowHistoryCount = Array.isArray(contact.warmupFlowHistory) ? contact.warmupFlowHistory.length : 0;
  const socials = contact.lead_socials && typeof contact.lead_socials === 'object'
    ? contact.lead_socials
    : parseJsonSafe(contact.lead_socials, {}) || {};
  const socialPlatforms = Object.keys(socials || {}).filter((k) => socials[k]);
  const createdMs = toEpochMs(contact.created_at || contact.createdAt || contact.created_timestamp);
  const createdRel = createdMs ? formatRelative(createdMs) : '';

  const cardClickable = !disabled && !selected;
  const handleCardClick = () => { if (cardClickable) onSelect && onSelect(); };
  const handleCardKey = (e) => {
    if (!cardClickable) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect && onSelect(); }
  };

  const cardClass = [
    'dup-merge-card',
    cardClickable ? 'dup-merge-card--pick' : '',
    selected ? 'dup-merge-card--selected' : '',
    failed ? 'dup-merge-card--failed' : '',
    disabled && !selected ? 'dup-merge-card--dim' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      role="button"
      tabIndex={cardClickable ? 0 : -1}
      onClick={handleCardClick}
      onKeyDown={handleCardKey}
      aria-pressed={selected}
      className={cardClass}
    >
      <div className="dup-merge-select">
        <input
          type="radio"
          className="dup-merge-radio"
          checked={selected}
          onChange={onSelect}
          disabled={disabled}
          onClick={(e) => e.stopPropagation()}
          aria-label={selected ? 'Is main contact' : 'Select as main contact'}
        />
        <span className="dup-merge-selectLabel">
          {selected
            ? (
              <span className="dup-merge-mainBadge">
                <i className="fa fa-crown" aria-hidden />
                Main contact
              </span>
            )
            : failed
              ? 'Merge failed'
              : 'Select as main contact'}
        </span>
      </div>

      <div className="dup-merge-cardHead">
        <div className="dup-merge-cardName" title={contact.business_name || ''}>
          {contact.business_name || '(no name)'}
        </div>
        {wasContacted && (
          <span className="dup-merge-sent" title="Already contacted">
            <i className="fa fa-paper-plane" aria-hidden />
            Contacted
          </span>
        )}
      </div>

      <div
        className="dup-merge-status"
        style={{
          color: statusStyle.color,
          background: hexToRgba(statusStyle.color, 0.1),
          border: `1px solid ${hexToRgba(statusStyle.color, 0.25)}`,
        }}
      >
        <i className={`fa ${statusStyle.icon}`} aria-hidden />
        <span>{statusStyle.label}</span>
      </div>

      {primaryEmail && (
        <div
          className={`dup-merge-chip${collisionSet.has(primaryEmail.toLowerCase()) ? ' dup-merge-chip--hit' : ''}`}
          title={primaryEmail}
        >
          <i className="fa fa-envelope" aria-hidden />
          <span className="dup-merge-chipText">{primaryEmail}</span>
          {extraEmailCount > 0 && (
            <span className="dup-merge-chipMore">+{extraEmailCount}</span>
          )}
        </div>
      )}

      {detailRows.length > 0 && (
        <div className="dup-merge-details">
          {detailRows.map((f) => (
            <div key={f.key} className="dup-merge-detail">
              <span className="dup-merge-detailLabel">{f.label}</span>
              <span className="dup-merge-detailValue">{String(f.value)}</span>
            </div>
          ))}
        </div>
      )}

      {socialPlatforms.length > 0 && (
        <div className="dup-merge-socials" title={`Social profiles: ${socialPlatforms.join(', ')}`}>
          {socialPlatforms.map((p) => (
            <span key={p} className="dup-merge-social">
              <i className={`fa-brands ${SOCIAL_ICON[p.toLowerCase()] || 'fa-globe'}`} aria-hidden />
            </span>
          ))}
        </div>
      )}

      {(dmCount > 0 || flowHistoryCount > 0 || notesCount > 0 || contactedRel || createdRel) && (
        <div className="dup-merge-meta">
          {dmCount > 0 && (
            <span className="dup-merge-pill" title="Decision makers">
              <i className="fa fa-user-tie" aria-hidden />
              {dmCount} {dmCount === 1 ? 'decision maker' : 'decision makers'}
            </span>
          )}
          {flowHistoryCount > 0 && (
            <span className="dup-merge-pill" title="Sent emails / flow steps">
              <i className="fa fa-paper-plane" aria-hidden />
              {flowHistoryCount} {flowHistoryCount === 1 ? 'email' : 'emails'}
            </span>
          )}
          {notesCount > 0 && (
            <span className="dup-merge-pill" title="Notes">
              <i className="fa fa-sticky-note" aria-hidden />
              {notesCount} {notesCount === 1 ? 'note' : 'notes'}
            </span>
          )}
          {contactedRel && (
            <span className="dup-merge-pill" title="Last contacted">
              <i className="fa fa-clock" aria-hidden />
              last {contactedRel}
            </span>
          )}
          {createdRel && (
            <span className="dup-merge-pill" title="Created">
              <i className="fa fa-plus-circle" aria-hidden />
              created {createdRel}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function hasValue(v) {
  return v != null && v !== '' && v !== '-' && v !== 'null';
}
function unionEmailList(a, b) {
  const parts = [
    ...(typeof a === 'string' ? a.split(/[,;]/) : []),
    ...(typeof b === 'string' ? b.split(/[,;]/) : []),
  ];
  const seen = new Set();
  const out = [];
  for (const raw of parts) {
    const t = String(raw || '').trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out.join(', ');
}
